import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { notificationService } from './notificationService';

class ApiService {
  private api: AxiosInstance;
  private baseURL: string;
  private authToken: string | null = null;
  private refreshToken: string | null = null;
  private isRefreshing: boolean = false;
  private failedQueue: Array<{
    resolve: (value?: any) => void;
    reject: (error?: any) => void;
  }> = [];

  constructor() {
    // Use the same base URL as the original app
    this.baseURL = process.env.NODE_ENV === 'production' 
      ? 'https://api.openana.ai/agent/nexus/api/v1' 
      : 'http://localhost:8008/api/v1';

    this.api = axios.create({
      baseURL: this.baseURL,
      timeout: 1000000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
    
    // Restore tokens from localStorage if available
    const storedToken = localStorage.getItem('access_token');
    const storedRefreshToken = localStorage.getItem('refresh_token');
    if (storedToken) {
      this.setAuthToken(storedToken);
      console.log('🔑 Restored auth token from localStorage');
    }
    if (storedRefreshToken) {
      this.refreshToken = storedRefreshToken;
      console.log('🔄 Restored refresh token from localStorage');
    }
  }

  // Authentication token management
  setAuthToken(token: string) {
    this.authToken = token;
    this.api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  }

  clearAuthToken() {
    this.authToken = null;
    delete this.api.defaults.headers.common['Authorization'];
  }

  getAuthToken(): string | null {
    return this.authToken;
  }

  private processQueue(error: any, token: string | null = null) {
    this.failedQueue.forEach(({ resolve, reject }) => {
      if (error) {
        reject(error);
      } else {
        resolve(token);
      }
    });
    
    this.failedQueue = [];
  }

  private clearTokens() {
    this.authToken = null;
    this.refreshToken = null;
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    delete this.api.defaults.headers.common['Authorization'];
  }

  private setupInterceptors() {
    // Request interceptor
    this.api.interceptors.request.use(
      (config) => {
        console.log(`🔄 API Request: ${config.method?.toUpperCase()} ${config.url}`);
        return config;
      },
      (error) => {
        console.error('❌ API Request Error:', error);
        return Promise.reject(error);
      }
    );

    // Response interceptor with automatic token refresh
    this.api.interceptors.response.use(
      (response: AxiosResponse) => {
        console.log(`✅ API Response: ${response.config.method?.toUpperCase()} ${response.config.url}`, response.status);
        return response;
      },
      async (error) => {
        const originalRequest = error.config;
        
        // Handle 401 errors with automatic token refresh
        if (error.response?.status === 401 && !originalRequest._retry && this.refreshToken) {
          console.log('🔄 Token expired, attempting refresh...');
          
          if (this.isRefreshing) {
            // If already refreshing, queue this request
            return new Promise((resolve, reject) => {
              this.failedQueue.push({ resolve, reject });
            }).then(token => {
              originalRequest.headers['Authorization'] = `Bearer ${token}`;
              return this.api(originalRequest);
            }).catch(err => {
              return Promise.reject(err);
            });
          }

          originalRequest._retry = true;
          this.isRefreshing = true;

          try {
            // Use raw axios to avoid triggering interceptors
            const response = await axios.post(`${this.baseURL}/auth/refresh`, { 
              refresh_token: this.refreshToken 
            }, {
              headers: { 'Content-Type': 'application/json' }
            });
            const { access_token, refresh_token } = response.data;
            
            // Update tokens
            this.setAuthToken(access_token);
            localStorage.setItem('access_token', access_token);
            
            if (refresh_token) {
              this.refreshToken = refresh_token;
              localStorage.setItem('refresh_token', refresh_token);
            }
            
            console.log('✅ Token refreshed successfully');
            
            // Process failed queue
            this.processQueue(null, access_token);
            
            // Retry original request
            originalRequest.headers['Authorization'] = `Bearer ${access_token}`;
            return this.api(originalRequest);
            
          } catch (refreshError) {
            console.error('❌ Token refresh failed:', refreshError);
            
            // Process failed queue with error
            this.processQueue(refreshError, null);
            
            // Clear tokens and redirect to login
            this.clearTokens();
            
            // Don't show notification for auth endpoints
            if (!originalRequest.url?.includes('/auth/')) {
              notificationService.show('Session expired. Please log in again.', 'error');
            }
            
            // Redirect to login page
            window.location.href = '/login';
            
            return Promise.reject(refreshError);
          } finally {
            this.isRefreshing = false;
          }
        }
        
        console.error('❌ API Response Error:', error);
        
        // Handle other error cases
        if (error.response) {
          const { status, data } = error.response;
          let errorMessage = data?.detail || data?.message || `HTTP ${status} Error`;
          
          switch (status) {
            case 400:
              errorMessage = data?.detail || 'Bad request';
              break;
            case 401:
              errorMessage = 'Unauthorized access';
              break;
            case 403:
              errorMessage = 'Access forbidden';
              break;
            case 404:
              errorMessage = 'Resource not found';
              break;
            case 500:
              errorMessage = 'Internal server error';
              break;
            default:
              errorMessage = `Server error (${status})`;
          }
          
          // Don't show notifications for certain endpoints
          const silentEndpoints = ['/health', '/chat/suggestions', '/auth/refresh'];
          const shouldShowNotification = !silentEndpoints.some(endpoint => 
            error.config?.url?.includes(endpoint)
          );
          
          if (shouldShowNotification) {
            notificationService.show(errorMessage, 'error');
          }
        } else if (error.request) {
          notificationService.show('Network error - please check your connection', 'error');
        } else {
          notificationService.show('Request failed', 'error');
        }
        
        return Promise.reject(error);
      }
    );
  }

  // Generic HTTP methods
  async get<T = any>(url: string, params?: any): Promise<T> {
    const response = await this.api.get(url, { params });
    return response.data;
  }

  async post<T = any>(url: string, data?: any, config?: any): Promise<T> {
    const response = await this.api.post(url, data, config);
    return response.data;
  }

  async put<T = any>(url: string, data?: any): Promise<T> {
    const response = await this.api.put(url, data);
    return response.data;
  }

  async delete<T = any>(url: string): Promise<T> {
    const response = await this.api.delete(url);
    return response.data;
  }

  // File upload with progress
  async uploadFile(url: string, formData: FormData, onProgress?: (progress: number) => void): Promise<any> {
    const response = await this.api.post(url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(progress);
        }
      },
    });
    return response.data;
  }

  // Health check
  async healthCheck(): Promise<any> {
    try {
      const response = await axios.get(
        process.env.NODE_ENV === 'production' ? '/health' : 'http://localhost:8008/health',
        { timeout: 5000 }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  // Document endpoints
  async getDocuments(): Promise<any> {
    return this.get('/documents/');
  }

  async getDocument(id: number): Promise<any> {
    return this.get(`/documents/${id}`);
  }

  async uploadDocument(file: File, onProgress?: (progress: number) => void): Promise<any> {
    const formData = new FormData();
    formData.append('file', file);
    return this.uploadFile('/documents/upload', formData, onProgress);
  }

  async uploadDocumentWithPassword(formData: FormData, onProgress?: (progress: number) => void): Promise<any> {
    return this.uploadFile('/documents/upload-with-password', formData, onProgress);
  }

  async deleteDocument(id: number): Promise<any> {
    return this.delete(`/documents/${id}`);
  }

  // Project endpoints
  async getProjects(): Promise<any> {
    return this.get('/projects/');
  }

  async getProject(id: number): Promise<any> {
    return this.get(`/projects/${id}`);
  }

  async createProject(projectData: any): Promise<any> {
    return this.post('/projects/', projectData);
  }

  async updateProject(id: number, projectData: any): Promise<any> {
    return this.put(`/projects/${id}`, projectData);
  }

  async deleteProject(id: number): Promise<any> {
    return this.delete(`/projects/${id}`);
  }

  async getProjectFiles(projectId: number): Promise<any> {
    return this.get(`/documents/project/${projectId}/files`);
  }

  async uploadToProject(formData: FormData, onProgress?: (progress: number) => void): Promise<any> {
    return this.uploadFile('/documents/upload-to-project', formData, onProgress);
  }

  async uploadToProjectWithPassword(formData: FormData, onProgress?: (progress: number) => void): Promise<any> {
    return this.uploadFile('/documents/upload-to-project-with-password', formData, onProgress);
  }

  async deleteProjectFile(projectId: number, fileId: number): Promise<any> {
    return this.delete(`/documents/project/${projectId}/files/${fileId}`);
  }

  async getProjectQueries(projectId: number): Promise<any> {
    return this.get(`/projects/${projectId}/queries`);
  }

  async createProjectQuery(projectId: number, queryData: any): Promise<any> {
    return this.post(`/projects/${projectId}/queries`, queryData);
  }

  async deleteProjectQuery(projectId: number, queryId: number): Promise<any> {
    return this.delete(`/projects/${projectId}/queries/${queryId}`);
  }

  // Chat endpoints
  async getChatSuggestions(): Promise<any> {
    return this.get('/chat/suggestions');
  }

  async getChatAnalytics(): Promise<any> {
    return this.get('/chat/analytics');
  }

  async sendChatQuery(queryData: any): Promise<any> {
    // Enhanced query data to request better source information
    const enhancedQueryData = {
      ...queryData,
      include_source_metadata: true,
      source_detail_level: 'full',
      include_document_info: true
    };
    
    console.log('🔍 Sending enhanced chat query:', enhancedQueryData);
    const response = await this.post('/chat/query', enhancedQueryData);
    
    // Log the response to see what we're getting back
    console.log('📥 Chat query response:', {
      message_length: response.response?.length || 0,
      sources_count: response.sources?.length || 0,
      sources_preview: response.sources?.map((s: any, i: number) => ({
        index: i,
        document_id: s.metadata?.document_id,
        page: s.metadata?.page,
        filename: s.metadata?.filename,
        has_text: !!s.text,
        text_length: s.text?.length || 0
      })) || []
    });
    
    return response;
  }

  async getChatHistory(limit: number = 50, projectId?: number): Promise<any> {
    const params: any = { limit };
    if (projectId) {
      params.project_id = projectId;
    }
    return this.get('/chat/history', params);
  }

  async getChatSessions(limit: number = 20, projectId?: number): Promise<any> {
    const params: any = { limit };
    if (projectId) {
      params.project_id = projectId;
    }
    return this.get('/chat/sessions', params);
  }

  async getSessionMessages(sessionId: number, limit: number = 50, offset: number = 0): Promise<any> {
    return this.get(`/chat/sessions/${sessionId}/messages`, { limit, offset });
  }

  async archiveChatSession(sessionId: number): Promise<any> {
    return this.post(`/chat/sessions/${sessionId}/archive`);
  }

  async deleteChatSession(sessionId: number): Promise<any> {
    return this.delete(`/chat/sessions/${sessionId}`);
  }

  // Processing endpoints
  async getProcessingStats(): Promise<any> {
    return this.get('/processing/stats');
  }

  async getProcessingJobs(): Promise<any> {
    return this.get('/processing/jobs');
  }

  async getProcessingJob(jobId: number): Promise<any> {
    return this.get(`/processing/jobs/${jobId}`);
  }

  async runEnhancedAnalysis(documentId: number): Promise<any> {
    return this.post(`/processing/documents/${documentId}/enhanced-analysis`);
  }

  async runRiskAnalysis(documentId: number): Promise<any> {
    return this.post(`/processing/documents/${documentId}/risk-analysis`);
  }

  async runBusinessInsights(documentId: number): Promise<any> {
    return this.post(`/processing/documents/${documentId}/business-insights`);
  }

  async runBatchEnhancedAnalysis(documentIds: number[]): Promise<any> {
    return this.post('/processing/batch/enhanced-analysis', documentIds);
  }

  async getEnhancedStats(): Promise<any> {
    return this.get('/processing/enhanced-stats');
  }

  // Utility methods
  getBaseURL(): string {
    return this.baseURL;
  }

  // Create a download URL for files
  getDownloadUrl(documentId: number): string {
    return `${this.baseURL}/documents/${documentId}/download`;
  }

  // Create a preview URL for files
  getPreviewUrl(documentId: number): string {
    return `${this.baseURL}/documents/${documentId}/preview`;
  }

  // Authentication endpoints
  async register(userData: any): Promise<any> {
    return this.post('/auth/register', userData);
  }

  async login(credentials: any): Promise<any> {
    const response = await this.post('/auth/login', credentials);
    
    // Store tokens after successful login
    if (response.access_token) {
      this.setAuthToken(response.access_token);
      localStorage.setItem('access_token', response.access_token);
    }
    
    if (response.refresh_token) {
      this.refreshToken = response.refresh_token;
      localStorage.setItem('refresh_token', response.refresh_token);
    }
    
    return response;
  }

  async logout(): Promise<any> {
    try {
      await this.post('/auth/logout');
    } finally {
      // Always clear tokens even if logout request fails
      this.clearTokens();
    }
  }

  async getCurrentUser(): Promise<any> {
    return this.get('/auth/me');
  }

  async updateProfile(userData: any): Promise<any> {
    return this.put('/auth/me', userData);
  }

  async refreshAccessToken(refreshToken: string): Promise<any> {
    return this.post('/auth/refresh', { refresh_token: refreshToken });
  }

  async requestPasswordReset(email: string, callbackUrl?: string): Promise<any> {
    const payload: any = { email };
    if (callbackUrl) {
      payload.callback_url = callbackUrl;
    }
    return this.post('/auth/password-reset-request', payload);
  }

  async resetPassword(token: string, newPassword: string): Promise<any> {
    return this.post('/auth/password-reset', { token, new_password: newPassword });
  }

  async verifyEmail(token: string): Promise<any> {
    return this.post('/auth/verify-email', { token });
  }

  async getGoogleAuthUrl(): Promise<any> {
    return this.get('/auth/google/login');
  }

  async handleGoogleCallback(code: string): Promise<any> {
    return this.post('/auth/google/callback', { code });
  }

  async getUserSessions(): Promise<any> {
    return this.get('/auth/sessions');
  }

  async revokeSession(sessionId: number): Promise<any> {
    return this.delete(`/auth/sessions/${sessionId}`);
  }

  async revokeAllSessions(): Promise<any> {
    return this.delete('/auth/sessions');
  }

  async getUserActivity(limit: number = 50, offset: number = 0): Promise<any> {
    return this.get('/auth/activity', { limit, offset });
  }

  // Cloud Integration endpoints
  async getIntegrations(): Promise<any> {
    return this.get('/integrations/');
  }

  async getIntegrationAuthUrl(provider: string): Promise<any> {
    return this.get(`/integrations/${provider}/auth-url`);
  }

  async connectIntegration(provider: string, code: string): Promise<any> {
    return this.post(`/integrations/${provider}/connect`, { code });
  }

  async disconnectIntegration(integrationId: number): Promise<any> {
    return this.delete(`/integrations/${integrationId}`);
  }

  async testIntegration(integrationId: number): Promise<any> {
    return this.post(`/integrations/${integrationId}/test`);
  }

  async getIntegrationFiles(provider: string, integrationId: number, folderId?: string): Promise<any> {
    const params = folderId ? { folder_id: folderId } : {};
    return this.get(`/integrations/${provider}/${integrationId}/files`, params);
  }

  async getIntegrationFolders(provider: string, integrationId: number, parentId?: string): Promise<any> {
    const params = parentId ? { parent_id: parentId } : {};
    return this.get(`/integrations/${provider}/${integrationId}/folders`, params);
  }

  async importFiles(integrationId: number, projectId: number, fileSelections: any[]): Promise<any> {
    return this.post('/integrations/import-files', {
      integration_id: integrationId,
      project_id: projectId,
      file_selections: fileSelections
    });
  }

  async getIntegrationQueryFiles(provider: string, integrationId: number, folderId?: string): Promise<any> {
    const params = folderId ? { folder_id: folderId } : {};
    return this.get(`/integrations/${provider}/${integrationId}/query-files`, params);
  }

  async importQueries(integrationId: number, projectId: number, fileSelections: any[]): Promise<any> {
    return this.post('/integrations/import-queries', {
      integration_id: integrationId,
      project_id: projectId,
      file_selections: fileSelections
    });
  }

  async importFolder(integrationId: number, projectId: number, folderId: string, folderName: string): Promise<any> {
    return this.post('/integrations/import-folder', {
      
      integration_id: integrationId,
      project_id: projectId,
      folder_id: folderId,
      folder_name: folderName
    });
  }

  // Structured Queries endpoints
  async createStructuredQuery(projectId: number, queryData: any): Promise<any> {
    return this.post(`/projects/${projectId}/structured-queries`, queryData);
  }

  async getStructuredQueries(projectId: number): Promise<any> {
    return this.get(`/projects/${projectId}/structured-queries`);
  }

  async getStructuredQuery(queryId: number): Promise<any> {
    return this.get(`/structured-queries/${queryId}`);
  }

  async runStructuredQuery(queryId: number): Promise<any> {
    return this.post(`/structured-queries/${queryId}/run`);
  }

  async runQueryCell(queryId: number, columnId: number, documentId: number, simpleMode: boolean = false): Promise<any> {
    return this.post(`/structured-queries/${queryId}/run-cell`, {
      column_id: columnId,
      document_id: documentId,
      simple_mode: simpleMode
    });
  }

  async addQueryColumn(queryId: number, columnData: any): Promise<any> {
    return this.post(`/structured-queries/${queryId}/add-column`, columnData);
  }

  async deleteStructuredQuery(queryId: number): Promise<any> {
    return this.delete(`/structured-queries/${queryId}`);
  }

  // Default project endpoints
  async getDefaultProject(): Promise<any> {
    return this.get('/projects/default');
  }

  async setDefaultProject(projectId: number): Promise<any> {
    return this.post(`/projects/${projectId}/set-default`);
  }

  // Document copying endpoints
  async copyDocumentToProject(documentId: number, data: { project_id: number }): Promise<any> {
    return this.post(`/documents/${documentId}/copy-to-project`, data);
  }

  async getUserAccessibleDocuments(search?: string, skip: number = 0, limit: number = 100): Promise<any> {
    const params: any = { skip, limit };
    if (search) {
      params.search = search;
    }
    return this.get('/documents/user/accessible', params);
  }
}

// Export singleton instance
export const apiService = new ApiService();
